"use client";

import { useState, useEffect } from "react";
import { useApp } from "@/context/AppContext";
import { useVehicles } from "@/context/VehicleContext";
import { Plus } from "lucide-react";
import {
  ServiceType,
  CustomerType,
  CustomerCategory,
  CustomerStatus,
  CustomerWithDetails,
  VehicleMakeOutput,
  VehicleModelOutput,
} from "@/types";
import { toast } from "sonner";
import CommonHeader from "./common/CommonHeader";
import { CustomersTable } from "./customer/CustomersTable";
import { CustomerFilter, CustomerFilters } from "./customer/CustomerFilter";
import {
  CustomerFormDialog,
  CustomerFormData,
} from "./customer/CustomerFormDialog";
import { CustomerDetailDialog } from "./customer/CustomerDetailDialog";
import { CustomerDeleteDialog } from "./customer/CustomerDeleteDialog";
import { userService } from "@/services/userService";
import { makeService } from "@/services/vehicle/makeService";
import { modelService } from "@/services/vehicle/modelService";
import { useDebounce } from "@/hooks/useDebounce";
import { CustomerQueryParams } from "@/types/customer.api.types";

export function CustomerManagement() {
  const {
    customers,
    addCustomer,
    updateCustomer,
    deleteCustomer,
    getVehiclesByCustomerId,
    addVehicle,
    appointments,
    subscriptions,
    staff,
    vehicles,
    refreshCustomers,
  } = useApp();
  const { refreshVehicles } = useVehicles();

  const [makes, setMakes] = useState<VehicleMakeOutput[]>([]);
  const [models, setModels] = useState<VehicleModelOutput[]>([]);
  const [phoneError, setPhoneError] = useState<string>("");
  const [refreshTrigger, setRefreshTrigger] = useState<number>(0);

  // Filter state
  const [filters, setFilters] = useState<CustomerFilters>({
    search: "",
    customerType: "all",
    customerCategory: "all",
    customerStatus: "all",
  });

  // Debounce search input to avoid excessive API calls
  const debouncedSearch = useDebounce(filters.search, 500);

  // Query params state (pagination and sorting)
  const [queryParams, setQueryParams] = useState<CustomerQueryParams>({
    page: 1,
    limit: 10,
    sortBy: "createdAt",
    sortOrder: "desc",
  });

  // Convert filter state to API query parameters
  const convertFiltersToParams = (): CustomerQueryParams => {
    const params: CustomerQueryParams = {
      page: queryParams.page || 1,
      limit: queryParams.limit || 10,
      sortBy: queryParams.sortBy || "createdAt",
      sortOrder: queryParams.sortOrder || "desc",
    };

    if (debouncedSearch.trim()) {
      params.search = debouncedSearch.trim();
    }

    if (filters.customerType !== "all") {
      params.customerType = filters.customerType as
        | "online-customer"
        | "offline-customer";
    }

    if (filters.customerCategory !== "all") {
      params.customerCategory = filters.customerCategory as
        | "regular-customer"
        | "occasional-customer"
        | "vip-customer";
    }

    if (filters.customerStatus !== "all") {
      params.customerStatus = filters.customerStatus as "active" | "inactive";
    }

    return params;
  };

  // Combine filters and query params
  const combinedParams = convertFiltersToParams();

  // Reset to page 1 when filters change (except pagination)
  useEffect(() => {
    setQueryParams((prev) => ({ ...prev, page: 1 }));
  }, [
    filters.customerType,
    filters.customerCategory,
    filters.customerStatus,
    debouncedSearch,
  ]);

  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDetailDialogOpen, setIsDetailDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] =
    useState<CustomerWithDetails | null>(null);

  const [formData, setFormData] = useState<CustomerFormData>({
    name: "",
    phone: "",
    email: "",
    address: "",
    city: "",
    notes: "",
    customerType: undefined,
    customerCategory: undefined,
    customerStatus: undefined,
    serviceProviders: [],
    // Vehicle fields
    vehiclePlateNumber: undefined,
    makeName: undefined,
    modelName: undefined,
    vehicleColor: undefined,
    vehicleType: undefined,
  });

  const fetchMakes = async () => {
    try {
      const data = await makeService.getAllMakes();
      setMakes(data);
    } catch (err) {
      console.error("Error fetching makes:", err);
      toast.error("Failed to load vehicle makes. Please try again.");
    }
  };

  const fetchModels = async () => {
    try {
      const data = await modelService.getAllModels();
      setModels(data);
    } catch (err) {
      console.error("Error fetching models:", err);
      toast.error("Failed to load vehicle models. Please try again.");
    }
  };

  useEffect(() => {
    fetchMakes();
    fetchModels();
  }, []);

  const validatePhone = (phone: string): boolean => {
    // Qatar phone format: +974 followed by exactly 8 digits (with or without spaces/dashes)
    const phoneRegex = /^\+974[\s-]?\d{4}[\s-]?\d{4}$/;
    return phoneRegex.test(phone);
  };

  const resetForm = () => {
    setFormData({
      name: "",
      phone: "",
      email: "",
      address: "",
      city: "",
      notes: "",
      customerType: undefined,
      customerCategory: undefined,
      customerStatus: undefined,
      serviceProviders: [],
      // Reset vehicle fields
      vehiclePlateNumber: undefined,
      makeName: undefined,
      modelName: undefined,
      vehicleColor: undefined,
      vehicleType: undefined,
    });
    setPhoneError("");
  };

  const handleAdd = async (data: CustomerFormData) => {
    if (!data.name || !data.phone) {
      toast.error("Please fill in all required fields");
      return;
    }

    // Validate phone number
    if (!validatePhone(data.phone)) {
      setPhoneError(
        "Please enter a valid Qatar phone number (e.g., +974 XXXX XXXX)",
      );
      toast.error("Invalid phone number format");
      return;
    }

    // Clear any previous errors
    setPhoneError("");

    // Create customer first
    const newCustomer = addCustomer({
      name: data.name,
      phone: data.phone,
      email: data.email || undefined,
      address: data.address || undefined,
      city: data.city || undefined,
    });

    // Check if vehicle data is provided
    const hasVehicleData = !!(
      data.vehiclePlateNumber ||
      data.makeName ||
      data.modelName ||
      data.vehicleColor ||
      data.vehicleType
    );

    // Create vehicle if vehicle data is provided
    await userService.createFullCustomer({
      name: data.name,
      phone: data.phone,
      email: data.email || undefined,
      address: data.address || undefined,
      customerType: data.customerType || undefined,
      customerCategory: data.customerCategory || undefined,
      customerStatus: data.customerStatus || undefined,
      serviceProviders: data.serviceProviders || undefined,
      vehiclePlateNumber: data.vehiclePlateNumber || undefined,
      makeName: data.makeName || undefined,
      modelName: data.modelName || undefined,
      vehicleColor: data.vehicleColor || undefined,
      vehicleType: data.vehicleType || undefined,
      year: data.vehicleYear || undefined,
      role: "CUSTOMER",
    });

    // Refresh customers in context and trigger table refresh
    await refreshCustomers();
    setRefreshTrigger((prev) => prev + 1);

    // Refresh vehicles if vehicle data was provided
    if (hasVehicleData) {
      await refreshVehicles();
    }

    toast.success("Customer and vehicle added successfully");

    setIsAddDialogOpen(false);
    resetForm();
  };

  const handleEdit = async (data: CustomerFormData) => {
    if (!selectedCustomer || !data.name || !data.phone) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      await userService.updateCustomer(selectedCustomer.id, {
        name: data.name,
        phone: data.phone,
        email: data.email || undefined,
        address: data.address || undefined,
        city: data.city || undefined,
        notes: data.notes || undefined,
        customerType: data.customerType,
        customerCategory: data.customerCategory,
        customerStatus: data.customerStatus,
        serviceProviders: data.serviceProviders || [],
      });

      // Refresh customers to get updated data
      await refreshCustomers();
      setRefreshTrigger((prev) => prev + 1);

      toast.success("Customer updated successfully");
      setIsEditDialogOpen(false);
      setSelectedCustomer(null);
      resetForm();
    } catch (err) {
      console.error("Error updating customer:", err);
      toast.error("Failed to update customer. Please try again.");
    }
  };

  const handleDelete = async () => {
    if (!selectedCustomer) return;

    try {
      await userService.deleteCustomer(selectedCustomer.id);
      // Refresh customers to get updated data
      await refreshCustomers();
      setRefreshTrigger((prev) => prev + 1);
      toast.success("Customer deleted successfully");
      setIsDeleteDialogOpen(false);
      setSelectedCustomer(null);
    } catch (err) {
      console.error("Error deleting customer:", err);
      toast.error("Failed to delete customer. Please try again.");
    }
  };

  const openEditDialog = (customer: CustomerWithDetails) => {
    setSelectedCustomer(customer);

    setFormData({
      name: customer.name,
      phone: customer.phone,
      email: customer.email || "",
      address: customer.address || "",
      city: customer.city || "",
      notes: customer.notes || "",
      customerType: customer.customerType,
      customerCategory: customer.customerCategory,
      customerStatus: customer.customerStatus,
      serviceProviders: customer.serviceProviders || [],
    });
    setIsEditDialogOpen(true);
  };

  const openDetailDialog = (customer: CustomerWithDetails) => {
    setSelectedCustomer(customer);
    setIsDetailDialogOpen(true);
  };

  const openDeleteDialog = (customer: CustomerWithDetails) => {
    setSelectedCustomer(customer);
    setIsDeleteDialogOpen(true);
  };

  return (
    <div className="space-y-6 w-full overflow-x-hidden">
      {/* Header */}
      <CommonHeader
        title="Customer Management"
        description="Manage your customer database"
        onClick={() => setIsAddDialogOpen(true)}
        buttonText="Add Customer"
        buttonIcon={Plus}
      />

      {/* Filters */}
      <CustomerFilter filters={filters} onFilterChange={setFilters} />

      {/* Customers Table */}
      <CustomersTable
        onDelete={openDeleteDialog}
        onView={openDetailDialog}
        onEdit={openEditDialog}
        refreshTrigger={refreshTrigger}
        filters={combinedParams}
        onFiltersChange={setQueryParams}
      />

      {/* Add Customer Dialog */}
      <CustomerFormDialog
        open={isAddDialogOpen}
        onOpenChange={(open) => {
          setIsAddDialogOpen(open);
          if (!open) {
            resetForm();
          }
        }}
        formData={formData}
        onSave={handleAdd}
        onCancel={() => {
          setIsAddDialogOpen(false);
          resetForm();
        }}
        mode="add"
        makes={makes}
        models={models}
        onMakesRefresh={fetchMakes}
        onModelsRefresh={fetchModels}
        phoneError={phoneError}
        onPhoneErrorClear={() => setPhoneError("")}
      />

      {/* Edit Customer Dialog */}
      <CustomerFormDialog
        open={isEditDialogOpen}
        onOpenChange={setIsEditDialogOpen}
        formData={formData}
        onSave={handleEdit}
        onCancel={() => {
          setIsEditDialogOpen(false);
          resetForm();
        }}
        mode="edit"
        makes={makes}
        models={models}
        onMakesRefresh={fetchMakes}
        onModelsRefresh={fetchModels}
        customerId={selectedCustomer?.id}
        customerVehicles={vehicles.filter(
          (v) => v.customerId === selectedCustomer?.id,
        )}
      />

      {/* Customer Detail Dialog */}
      <CustomerDetailDialog
        open={isDetailDialogOpen}
        onOpenChange={setIsDetailDialogOpen}
        customer={selectedCustomer as any}
        vehicles={vehicles}
        appointments={appointments}
        staff={staff}
      />

      {/* Delete Confirmation Dialog */}
      <CustomerDeleteDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        customer={selectedCustomer as any}
        onConfirm={handleDelete}
      />
    </div>
  );
}
